import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { keyResultService, okrService, departamentoService, empresaService } from '@/services/api';
import { KeyResult, OKR, Departamento, Empresa } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { CheckCircle, XCircle, Clock, Target, User, Calendar, Building2, Paperclip, ExternalLink, History } from 'lucide-react';
import { format } from 'date-fns';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function Aprovacoes() {
  const { user, empresaAtiva } = useAuth();
  const [pendingKRs, setPendingKRs] = useState<KeyResult[]>([]);
  const [historyKRs, setHistoryKRs] = useState<KeyResult[]>([]);
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingKR, setRejectingKR] = useState<KeyResult | null>(null);
  const [rejectMotivo, setRejectMotivo] = useState('');

  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';

  const loadData = async () => {
    if (!user) return;

    try {
      const activeId = empresaAtiva?.id || '';
      const [pendingRes, historyRes, okrsRes, depsRes, empresasRes] = await Promise.all([
        user.tipo === 'superadmin'
          ? keyResultService.getPendingApprovals()
          : keyResultService.getPendingApprovals(activeId),
        user.tipo === 'superadmin'
          ? keyResultService.getApprovalHistory()
          : keyResultService.getApprovalHistory(activeId),
        user.tipo === 'superadmin'
          ? okrService.getAll()
          : okrService.getByEmpresa(activeId),
        user.tipo === 'superadmin'
          ? departamentoService.getAll()
          : departamentoService.getByEmpresa(activeId),
        empresaService.getAll(),
      ]);

      if (pendingRes.success) setPendingKRs(pendingRes.data);
      if (historyRes.success) {
        // Sort by evaluation date, most recent first
        const sorted = historyRes.data.sort((a, b) => {
          const dateA = a.aprovacao?.avaliado_em ? new Date(a.aprovacao.avaliado_em).getTime() : 0;
          const dateB = b.aprovacao?.avaliado_em ? new Date(b.aprovacao.avaliado_em).getTime() : 0;
          return dateB - dateA;
        });
        setHistoryKRs(sorted);
      }
      if (okrsRes.success) setOkrs(okrsRes.data);
      if (depsRes.success) setDepartamentos(depsRes.data);
      if (empresasRes.success) setEmpresas(empresasRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user, empresaAtiva]);

  const handleApprove = async (kr: KeyResult) => {
    if (!user) return;
    
    const result = await keyResultService.approveCompletion(kr.id, user.nome);
    if (result.success) {
      toast.success('Key Result aprovado com sucesso');
      loadData();
    } else {
      toast.error('Erro ao aprovar Key Result');
    }
  };

  const openRejectDialog = (kr: KeyResult) => {
    setRejectingKR(kr);
    setRejectMotivo('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectingKR || !user || !rejectMotivo.trim()) return;
    
    const result = await keyResultService.rejectCompletion(rejectingKR.id, user.nome, rejectMotivo);
    if (result.success) {
      toast.success('Key Result rejeitado');
      setRejectDialogOpen(false);
      setRejectingKR(null);
      loadData();
    } else {
      toast.error('Erro ao rejeitar Key Result');
    }
  };

  const getOKR = (okrId: string) => okrs.find(o => o.id === okrId);
  const getDepartamento = (depId: string) => departamentos.find(d => d.id === depId);
  const getEmpresa = (empresaId: string) => empresas.find(e => e.id === empresaId);

  if (!isAdmin) {
    return <Navigate to="/okrs" replace />;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  const renderKRCard = (kr: KeyResult, showActions: boolean = true) => {
    const okr = getOKR(kr.okr_id);
    const dep = okr ? getDepartamento(okr.departamento_id) : null;
    const empresa = okr ? getEmpresa(okr.empresa_id) : null;
    const isApproved = kr.aprovacao?.status === 'approved';
    const isRejected = kr.aprovacao?.status === 'rejected';

    return (
      <Card key={kr.id} className="card-interactive">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                {kr.aprovacao?.status === 'pending' && (
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                    <Clock className="h-3 w-3 mr-1" />
                    Aguardando Aprovação
                  </Badge>
                )}
                {isApproved && (
                  <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Aprovado
                  </Badge>
                )}
                {isRejected && (
                  <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                    <XCircle className="h-3 w-3 mr-1" />
                    Recusado
                  </Badge>
                )}
              </div>
              <CardTitle className="text-base">{kr.descricao}</CardTitle>
              {okr && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Target className="h-4 w-4" />
                  <span>{okr.titulo}</span>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {empresa && user?.tipo === 'superadmin' && (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{empresa.nome}</span>
              </div>
            )}
            {dep && (
              <div>
                <span className="text-muted-foreground">Departamento:</span>
                <span className="ml-1 font-medium">{dep.nome}</span>
              </div>
            )}
            {kr.aprovacao?.solicitado_por && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{kr.aprovacao.solicitado_por}</span>
              </div>
            )}
            {kr.aprovacao?.solicitado_em && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{format(new Date(kr.aprovacao.solicitado_em), 'dd/MM/yyyy HH:mm')}</span>
              </div>
            )}
          </div>

          <div className="p-3 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Progresso solicitado:</span>
              <div className="flex items-center gap-2">
                {kr.aprovacao?.progresso_solicitado === kr.progresso_meta && (
                  <Badge className="bg-green-100 text-green-800">100%</Badge>
                )}
                <span className="font-medium">
                  {kr.aprovacao?.progresso_solicitado} / {kr.progresso_meta}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-muted-foreground">Progresso atual:</span>
              <span className="font-medium">{kr.progresso_atual} / {kr.progresso_meta}</span>
            </div>
          </div>

          {/* Observação do solicitante */}
          {kr.aprovacao?.observacao && (
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm font-medium text-muted-foreground">Observação:</p>
              <p className="text-sm mt-1">{kr.aprovacao.observacao}</p>
            </div>
          )}

          {/* History info - show evaluator and date */}
          {(isApproved || isRejected) && kr.aprovacao?.avaliado_em && (
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Avaliado por:</span>
                <span className="font-medium">{kr.aprovacao.avaliado_por}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-muted-foreground">Data:</span>
                <span className="font-medium">{format(new Date(kr.aprovacao.avaliado_em), 'dd/MM/yyyy HH:mm')}</span>
              </div>
            </div>
          )}

          {/* Rejection reason */}
          {isRejected && kr.aprovacao?.motivo_rejeicao && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm font-medium text-red-700 dark:text-red-400">Motivo da recusa:</p>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">{kr.aprovacao.motivo_rejeicao}</p>
            </div>
          )}

          {kr.anexo_url && (
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Anexo:</span>
                  <span className="text-sm font-medium">{kr.anexo_nome || 'arquivo'}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1"
                  onClick={() => window.open(kr.anexo_url, '_blank')}
                >
                  <ExternalLink className="h-3 w-3" />
                  Abrir
                </Button>
              </div>
            </div>
          )}

          {kr.anexo_obrigatorio && !kr.anexo_url && showActions && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <Paperclip className="h-4 w-4" />
                <span className="text-sm font-medium">Atenção: Este Key Result exige anexo mas nenhum foi enviado.</span>
              </div>
            </div>
          )}

          {showActions && (
            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => openRejectDialog(kr)}
                className="gap-2"
              >
                <XCircle className="h-4 w-4" />
                Recusar
              </Button>
              <Button
                onClick={() => handleApprove(kr)}
                className="gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Aprovar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Aprovações</h1>
          <p className="text-muted-foreground">
            Gerencie as solicitações de conclusão de Key Results
          </p>
        </div>

        <Tabs defaultValue="pendentes" className="w-full">
          <TabsList>
            <TabsTrigger value="pendentes" className="gap-2">
              <Clock className="h-4 w-4" />
              Pendentes
              {pendingKRs.length > 0 && (
                <Badge variant="secondary" className="ml-1">{pendingKRs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="historico" className="gap-2">
              <History className="h-4 w-4" />
              Histórico
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pendentes" className="mt-6">
            {pendingKRs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Nenhuma aprovação pendente</p>
                  <p className="text-muted-foreground text-sm">
                    Todas as solicitações foram processadas
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingKRs.map((kr) => renderKRCard(kr, true))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="historico" className="mt-6">
            {historyKRs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <History className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Nenhum histórico</p>
                  <p className="text-muted-foreground text-sm">
                    Você ainda não processou nenhuma solicitação
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {historyKRs.map((kr) => renderKRCard(kr, false))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recusar Conclusão</DialogTitle>
            <DialogDescription>
              Informe o motivo da recusa para que o usuário possa corrigir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {rejectingKR && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-medium">{rejectingKR.descricao}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Progresso solicitado: {rejectingKR.aprovacao?.progresso_solicitado} / {rejectingKR.progresso_meta}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo da Recusa *</Label>
              <Textarea
                id="motivo"
                placeholder="Explique o motivo da recusa..."
                value={rejectMotivo}
                onChange={(e) => setRejectMotivo(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleReject}
              disabled={!rejectMotivo.trim()}
              variant="destructive"
            >
              Recusar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
